import {
  AppLogo,
  Button,
  Icon,
  NotificationsToggle,
  Pill,
  RefreshButton,
  Select,
  SidebarToggle,
  Topbar,
  UpdateBadge,
  UserChip,
  t,
  toast,
  type NotificationsState,
  type Role,
} from "@mypos/ui";
import { useStoreSwitcher } from "@till/adapters/useStoreSwitcher";
import { useDrawerStore } from "@till/lib/stores/drawerStore";
import { useAuthStore } from "../lib/authStore";
import { useLocaleStore } from "../lib/localeStore";
import { useInstallPrompt } from "../lib/useInstallPrompt";
import { useNotifications } from "../lib/useNotifications";
import { useUpdater } from "../lib/useUpdater";

export function ManagerTopbar() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const locale = useLocaleStore((s) => s.locale);
  const setLocale = useLocaleStore((s) => s.setLocale);
  const { canInstall, promptInstall } = useInstallPrompt();
  const { state, subscribe, unsubscribe } = useNotifications();
  const updater = useUpdater();
  const drawerOpen = useDrawerStore((s) => !!s.activeSessionId);

  const storeId = user?.storeId ?? null;
  const isManager = user?.role === "manager";
  const { stores: storeOptions, busy: switchingStore, switchTo } = useStoreSwitcher({
    currentStoreId: storeId,
    userId: user?.id,
  });
  const currentStoreName = storeOptions.find((s) => s.id === storeId)?.name ?? null;
  const onSelectStore = (id: string) => {
    if (!id || id === storeId) return;
    if (drawerOpen) {
      // In-app toast, not window.alert() — the native dialog renders as
      // "localhost says…" in the browser, which reads as a Chrome error.
      toast.error(t("topbar.shiftBlocksStoreSwitch"));
      return;
    }
    void switchTo(id);
  };

  return (
    <Topbar
      left={
        <div className="flex items-center gap-2">
          <SidebarToggle />
          <AppLogo size="sm" showText={false} />
          {isManager && storeOptions.length > 0 ? (
            <Select
              value={storeId && storeOptions.some((s) => s.id === storeId) ? storeId : ""}
              onChange={(e) => onSelectStore(e.target.value)}
              options={[
                ...(!storeId || !storeOptions.some((s) => s.id === storeId)
                  ? [{ value: "", label: t("topbar.selectStore") }]
                  : []),
                ...storeOptions.map((s) => ({ value: s.id, label: s.name })),
              ]}
              disabled={switchingStore}
              aria-label={t("topbar.activeStore")}
              style={{ maxWidth: 180 }}
              title={drawerOpen ? t("topbar.shiftBlocksStoreSwitch") : undefined}
            />
          ) : storeId ? (
            <Pill tone="neutral">
              {t("topbar.store")}: <span title={storeId}>{currentStoreName ?? storeId.slice(0, 8)}</span>
            </Pill>
          ) : null}
        </div>
      }
      right={
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5 sm:gap-2.5">
          <Select
            value={locale}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "en" || v === "lo") setLocale(v);
            }}
            options={[
              { value: "en", label: "EN" },
              { value: "lo", label: "ລາວ" },
            ]}
          />
          {canInstall && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void promptInstall()}
              icon={<Icon name="package-plus" size="sm" />}
              aria-label={t("topbar.install")}
            >
              <span className="hidden sm:inline">{t("topbar.install")}</span>
            </Button>
          )}
          {updater.available && (
            <UpdateBadge
              status={updater.status}
              onCheck={updater.check}
              onDownload={updater.download}
              onInstall={updater.install}
              hideWhenIdle
            />
          )}
          <NotificationsToggle
            state={state as NotificationsState}
            onEnable={() => void subscribe()}
            onDisable={() => void unsubscribe()}
          />
          <RefreshButton />
          {user && (
            <span className="hidden md:inline-flex">
              <UserChip user={{ name: user.name, role: user.role as Role }} />
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void logout()}
            icon={<Icon name="log-out" size="sm" />}
            aria-label={t("common.signOut")}
          >
            <span className="hidden sm:inline">{t("common.signOut")}</span>
          </Button>
        </div>
      }
    />
  );
}
